import React, { PropsWithChildren, useEffect, useState } from 'react';
import './Dashboard.scss';
import { useParams } from 'react-router';
import { DashboardViewModel } from '@views';
import { DashboardViewCreator, InfoPanelViewCreator, JoyrideContentViewCreator } from '@viewCreators';
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { DebugPanel, InfoDialog } from '@components';
import { DateTime } from 'luxon';
import logger from '@utils/logger';
import { Joyride, ACTIONS, LIFECYCLE, type EventData, type Step } from 'react-joyride';
import { palette } from '@app/palette';
import { ToDate, ToDateTime } from '@utils/DateUtils';
import { DashboardHeader } from './DashboardHeader';
import { GoalTrackerCard2 } from '@components/GoalTracking';
import { PaymentModal } from '@components/modals';
import { PotCard2 } from '@components/PotCard/PotCard2';
import { ScienceSomethingCard2 } from '@components/ScienceSomething/ScienceSomethingCard2';
import { ExerciseInDisguiseCard2 } from '@components/ExerciseInDeisguise/ExerciseInDisguiseCard2';
import { LeaderCard2 } from '@app/components/Leaderboard/LeaderCard2';
import { SurveyDrawer2 } from '@components/SurveyDrawer/SurveyDrawer2';
import { SurveyDrawerContextProvider } from '@app/components/SurveyDrawer/SurveyDrawerControllerProvider';
import { NavigationEligibilityProvider } from '@components/SurveyDrawer/NavigationEligibilityContextProvider';
import { TeamCard } from '@app/components/TeamCard';
import { useAppCookies } from '@app/providers/AppCookieProvider';

export const Dashboard: React.FC<PropsWithChildren> = () => {
  const cookies = useAppCookies();
  const params = useParams();
  const [view, setView] = useState(DashboardViewModel.Empty);

  const [groupStartDate, setGroupStartDate] = useState<DateTime>();

  const [reload, setReload] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [steps, setSteps] = useState<Step[]>([]);

  const [infoWithDismissedStatus, setInfoWithDismissedStatus] = useState<
    {
      content: any | undefined;
      dismissed: boolean;
      offset: number;
      title: string;
    }[]
  >([]);
  const [infoDialogState, setInfoDialogState] = useState<{
    open: boolean;
    infoDialogContent: { title: string; content: any; offset: number } | undefined;
  }>({ open: false, infoDialogContent: undefined });

  const reloadDashboardView = () => {
    setReload(true);
  };

  useEffect(() => {
    // Check if there's another info item to display
    const nextInfo = infoWithDismissedStatus.filter((info) => !info.dismissed).sort((a, b) => a.offset - b.offset)[0];

    // If there is an info item to display, set the dialog state and open the dialog
    if (nextInfo) {
      setInfoDialogState((prev) => ({
        ...prev,
        open: Boolean(nextInfo.content) && !nextInfo.dismissed,
        infoDialogContent: {
          title: nextInfo.title,
          content: nextInfo.content,
          offset: nextInfo.offset,
        },
      }));
    }
  }, [infoWithDismissedStatus]);

  useEffect(() => {
    if (!reload) return;

    const loadJoyrideContent = () => {
      return new JoyrideContentViewCreator().createView().then(setSteps);
    };

    const loadDashboardView = async () => {
      let result;
      try {
        result = await new DashboardViewCreator().createViewWithArg({
          groupId: Number(params['groupId']),
          userId: String(cookies.profile.id),
        });
      } catch (error: any) {
        logger.error('Failed to load dashboard view', error);
        if (error.message && error.message.includes('You are not authorized to view this group')) {
          setForbidden(true);
        }
        return;
      }
      return result;
    };

    const loadInfoPanel = async (groupStartDate: DateTime, groupId: number) => {
      // Get all the info panel items
      const info = await new InfoPanelViewCreator().createView(ToDateTime(groupStartDate), groupId);

      // Set the info items with their dismissed status in state. This will trigger the info dialog to show if needed
      setInfoWithDismissedStatus(info);
    };

    const initializeViews = async () => {
      const result = await loadDashboardView();
      if (!result) {
        return;
      }

      // promises
      await Promise.all([loadInfoPanel(ToDateTime(result.groupStartDate), result.groupId), loadJoyrideContent()]);

      // Convert the group start date via ToDate (ensures we don't have UTC -> local timezone conversion issues)
      const luxonGroupStartDate = ToDate(result.groupStartDate);

      // Update result with the new group start date
      result.groupStartDate = luxonGroupStartDate;
      setGroupStartDate(luxonGroupStartDate);

      // Set the view state
      setView(result);

      setIsLoading(false);
    };

    initializeViews();
    setReload(false);
  }, [reload]);

  const backgroundStyles: SxProps = {
    background: '#f4f4f4',
    width: '100vw',
    maxWidth: '100%',
    color: palette.primary,
    paddingBottom: '50px',
  };

  const [runJoyride, setRunJoyride] = useState<boolean>(true);
  const handleJoyrideCallback = (data: EventData) => {
    const cohortId = view.groupId;
    if (!cohortId || cohortId === 0) {
      // Client is not ready yet
      return;
    }
    const { action, lifecycle, index } = data;

    // If the user skips the joyride, we should skip it next time they visit the app
    if (action === ACTIONS.SKIP) {
      localStorage.setItem(`dashboard:joyrideskipped${cohortId}`, 'true');
      setRunJoyride(false);
      return;
    } else if (lifecycle === LIFECYCLE.COMPLETE && index === steps.length - 1) {
      // Note: Lifecycle is set to complete after each step of the joyride,
      // so we need to use the index to determine if the last step was completed

      // Ensure the user doesn't see the joyride again
      localStorage.setItem(`dashboard:joyrideskipped${cohortId}`, 'true');

      // Set run to false so the joyride doesn't show again. This also triggers the info dialog to show
      setRunJoyride(false);

      return;
    } else if (action === ACTIONS.START) {
      const joyrideSkipped = localStorage.getItem(`dashboard:joyrideskipped${cohortId}`);
      if (joyrideSkipped) {
        setRunJoyride(false);
      }
    }
  };

  useEffect(() => {
    // Trigger the info dialog if the joyride is skipped or ends
    if (!runJoyride && infoDialogState.infoDialogContent) {
      setInfoDialogState((prevState) => ({ ...prevState, open: true }));
    }
  }, [runJoyride, infoDialogState.infoDialogContent]);

  if (isLoading) {
    return (
      <Container sx={backgroundStyles}>
        <Stack direction={'column'} gap={'16px'}>
          <DashboardHeader loading={isLoading} view={view} />
          <Typography variant="h6" align="center">
            Loading dashboard...
          </Typography>
        </Stack>
      </Container>
    );
  } else {
    return (
      <NavigationEligibilityProvider>
        <SurveyDrawerContextProvider groupId={view.groupId}>
          <Joyride
            continuous={true}
            steps={steps}
            run={runJoyride}
            options={{ buttons: ['skip', 'back', 'close', 'primary'] }}
            styles={{ buttonPrimary: { backgroundColor: palette.primary.main } }}
            onEvent={handleJoyrideCallback}
            locale={{
              last: 'Done',
            }}
          />

          <Container sx={backgroundStyles}>
            <Stack direction={'column'} gap={'16px'}>
              <DashboardHeader loading={isLoading} view={view} />
              <div className="teamCardJoyride dashboard-card">
                <TeamCard
                  participants={view.team.participants}
                  cohortId={view.groupId}
                  cohortName={view.groupName}
                  accountabilityPartners={view.accountabilityPartners}
                ></TeamCard>
              </div>
              {view.cohortUserId && groupStartDate && (
                <div className="goalsCardJoyride dashboard-card">
                  <GoalTrackerCard2
                    groupId={view.groupId}
                    groupStartDate={groupStartDate}
                    cohortUserId={view.cohortUserId}
                    reloadDashboardView={reloadDashboardView}
                    weekNumberIndexZero={view.currentWeekNumberIndexZero}
                  ></GoalTrackerCard2>
                </div>
              )}
              <div className="potCardJoyride dashboard-card">
                <PotCard2 groupId={view.groupId} groupStartDate={view.groupStartDate}></PotCard2>
              </div>
              {groupStartDate && (
                <div className="exerciseInDisguiseJoyride dashboard-card">
                  <ExerciseInDisguiseCard2 groupStart={view.groupStartDate}></ExerciseInDisguiseCard2>
                </div>
              )}
              {groupStartDate && (
                <div className="scienceSomethingJoyride dashboard-card">
                  <ScienceSomethingCard2 startDate={view.groupStartDate} groupId={view.groupId}></ScienceSomethingCard2>
                </div>
              )}
              <div className="leaderCardJoyride dashboard-card">
                <LeaderCard2 groupId={view.groupId} startDate={view.groupStartDate}></LeaderCard2>
              </div>
              <DebugPanel displayItem={view} />
            </Stack>
          </Container>
          {view.needPayment && view.individualPledgeAmount > 0 && (
            <PaymentModal
              cohortUserId={view.cohortUserId}
              groupId={view.groupId}
              open={view.needPayment}
              amount={view.individualPledgeAmount}
              cohortWeek={view.currentWeekNumberIndexZero}
            />
          )}
          <SurveyDrawer2 />

          {/* Only show the InfoDialog if the Joyride isn't running */}
          {!runJoyride && (
            <InfoDialog
              message={infoDialogState.infoDialogContent?.content ?? ''}
              title={infoDialogState.infoDialogContent?.title ?? ''}
              onDismiss={() => {
                if (infoDialogState.infoDialogContent && infoDialogState.infoDialogContent.title) {
                  localStorage.setItem(
                    `dashboard:info:${infoDialogState.infoDialogContent.title}|${view.groupId}`,
                    'true'
                  );
                }
                setInfoDialogState({ ...infoDialogState, open: false });
                setInfoWithDismissedStatus((prev) =>
                  prev.map((info) => {
                    if (info.title === infoDialogState.infoDialogContent?.title) {
                      return { ...info, dismissed: true };
                    }
                    return info;
                  })
                );
              }}
              onClose={() => {
                setInfoDialogState({ ...infoDialogState, open: false });
              }}
              onReadLater={() => setInfoDialogState({ ...infoDialogState, open: false })}
              open={infoDialogState.open}
            ></InfoDialog>
          )}
          {forbidden && (
            <Dialog
              onClose={() => {
                setForbidden(false);
                window.location.href = '/groups';
              }}
              open={forbidden}
            >
              <DialogTitle>OOPS!</DialogTitle>
              <DialogContent>
                <Typography>
                  You requested to stop or pause your membership on this team. If we got that wrong, email{' '}
                  <a href="mailto:admin@wehateexercise.com">admin@wehateexercise.com</a> so we can make it right!
                </Typography>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center' }}>
                <Button
                  variant={'contained'}
                  onClick={() => {
                    setForbidden(false);
                    window.location.href = '/groups';
                  }}
                >
                  OK
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </SurveyDrawerContextProvider>
      </NavigationEligibilityProvider>
    );
  } // <-- Ends else block for isLoading check
};
